import { useCallback, useEffect, useRef } from 'react';

export interface OrganizationActionEpoch {
  organizationId: string;
  epoch: number;
}

/**
 * Fences an awaited organization-scoped action against tenant switches and
 * unmounts. The epoch also catches A -> B -> A while the original A request is
 * still pending, which a plain organization-id comparison cannot distinguish.
 */
export function useOrganizationActionFence(organizationId: string | undefined) {
  const scopeRef = useRef({ organizationId, epoch: 0 });
  const mountedRef = useRef(false);

  if (scopeRef.current.organizationId !== organizationId) {
    scopeRef.current = {
      organizationId,
      epoch: scopeRef.current.epoch + 1,
    };
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const beginOrganizationAction =
    useCallback((): OrganizationActionEpoch | null => {
      const current = scopeRef.current;
      if (!current.organizationId) return null;
      return {
        organizationId: current.organizationId,
        epoch: current.epoch,
      };
    }, []);

  const isOrganizationActionCurrent = useCallback(
    (
      action: OrganizationActionEpoch | null
    ): action is OrganizationActionEpoch => {
      if (!action || !mountedRef.current) return false;
      const current = scopeRef.current;
      return (
        current.organizationId === action.organizationId &&
        current.epoch === action.epoch
      );
    },
    []
  );

  const invalidateOrganizationActions = useCallback(() => {
    const current = scopeRef.current;
    scopeRef.current = {
      organizationId: current.organizationId,
      epoch: current.epoch + 1,
    };
  }, []);

  return {
    beginOrganizationAction,
    isOrganizationActionCurrent,
    invalidateOrganizationActions,
  };
}
